import { useRef, useState } from 'react';

import { toUserMessage } from '@platform/api-client';
import { kstToday } from '@platform/datetime';

import {
  useCreatePumsemEdition,
  useRemovePumsemEdition,
  useSetPumsemEditionVisibility,
  useUpdatePumsemEdition,
} from '../../api/use-pumsem-manage.query';
import type { PumsemEdition } from '../../model/pumsem.type';
import {
  BTN_DANGER,
  BTN_GHOST,
  BTN_PRIMARY,
  FIELD,
  LABEL,
} from './pumsemFormStyles';

interface PumsemEditionPanelProps {
  editions: PumsemEdition[];
  /** null = "최신 공개판"(고르지 않은 상태). */
  selected: string | null;
  onSelect: (slug: string | null) => void;
  /** 실제로 보고 있는 판. 서버가 정한 최신 공개판일 수 있다. */
  active: string;
  /** 이 판이 가진 항목 수. 삭제 확인에 실제 규모를 적기 위해 받는다. */
  itemCount: number;
}

const CURRENT_YEAR = Number(kstToday().split('-')[0]);
/** 표준품셈 발행 이전 연도는 오타다. `Number('')` 가 0 인 것부터 막는다. */
const MIN_YEAR = 2000;

/**
 * 개정판 관리 — 추가 / 이름 수정 / 공개·숨김 / 삭제.
 *
 * ── 판이 화면의 주어다 ──────────────────────────────────────
 * 아래 부문·장절·항목이 전부 여기서 고른 판에 매인다. 그래서 맨 위에 있다.
 *
 * `slug`(2026-1)는 고칠 수 없다 — 판 고정 링크가 그 값을 물고 있다. 고칠 수
 * 있는 건 표기 이름뿐이고, 그래서 수정 폼에 입력칸이 하나다.
 *
 * ── 공개는 토글, 삭제는 두 단계 ─────────────────────────────
 * 예전에는 "발행"이 편도였다. 그러면 오탈자를 발견해도 내릴 방법이 없어
 * 공개된 판을 켜둔 채 고치게 된다. 이제 공개/숨김을 오갈 수 있고,
 * **삭제는 숨긴 판에서만** 된다 — 확인이 두 단계가 되어 살아 있는 판을 한 번의
 * 클릭으로 날리지 않는다.
 */
export function PumsemEditionPanel({
  editions,
  selected,
  onSelect,
  active,
  itemCount,
}: PumsemEditionPanelProps) {
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingHide, setConfirmingHide] = useState(false);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [half, setHalf] = useState<'H1' | 'H2'>('H1');
  /**
   * 새 판을 어느 판에서 복사할지. 기본은 **최신 판**이다 — 반기 개정에서 실제로
   * 바뀌는 항목은 극소수라, 빈 판에서 시작하면 안 바뀐 항목까지 전부 다시
   * 넣어야 하고 그 과정에서 오타가 섞인다.
   */
  const [copyFrom, setCopyFrom] = useState<string>('');
  const [label, setLabel] = useState('');

  // 인라인 편집을 닫으면 `autoFocus` 입력이 언마운트되면서 포커스가 <body> 로
  // 떨어진다. 키보드·스크린리더 사용자는 그때마다 페이지 맨 위로 돌아간다.
  const addRef = useRef<HTMLButtonElement>(null);
  const renameRef = useRef<HTMLButtonElement>(null);

  const createEdition = useCreatePumsemEdition();
  const updateEdition = useUpdatePumsemEdition();
  const setVisibility = useSetPumsemEditionVisibility();
  const removeEdition = useRemovePumsemEdition();

  const current = editions.find((item) => item.slug === active);
  // 공개 목록에는 이 필드가 없다. 관리 목록에서만 공개 여부를 안다.
  const isHidden = current?.isPublished === false;
  const onError = (cause: unknown) => setError(toUserMessage(cause));

  /** 이 판을 숨기면 사용자가 대신 보게 될 판. 숨기기 확인 문구에 쓴다. */
  const fallback = editions.find(
    (item) => item.slug !== current?.slug && item.isPublished !== false,
  );

  /**
   * `useState` 초깃값은 다시 계산되지 않는다. 첫 렌더에서 `editions` 는 아직
   * 빈 배열이라 예전 코드의 `editions[0]?.slug ?? ''` 는 **항상 빈 문자열**로
   * 굳었다 — 주석은 "기본은 최신 판 복사" 라고 약속해 놓고 실제로는 늘 빈 판이
   * 만들어졌고, 그걸 모른 채 임포트를 시작하면 직전 판에만 있던 항목이 통째로
   * 사라진다. 그래서 폼을 **열 때** 최신 판으로 맞춘다.
   */
  const openAddForm = () => {
    setCopyFrom(editions[0]?.slug ?? '');
    setYear(CURRENT_YEAR);
    setAdding(true);
  };

  return (
    <section className="mb-8 rounded-xl border border-neutral-200 p-4 dark:border-white/12">
      <h2 className={LABEL}>개정판</h2>

      <div className="flex flex-wrap items-center gap-2">
        <select
          className={`${FIELD} w-auto min-w-52`}
          value={selected ?? ''}
          aria-label="개정판 선택"
          onChange={(event) => {
            setError(null);
            setRenaming(false);
            setConfirmingDelete(false);
            onSelect(event.target.value || null);
          }}
        >
          <option value="">최신 공개판</option>
          {editions.map((item) => (
            <option key={item.slug} value={item.slug}>
              {item.label}
              {item.isPublished === false ? ' (숨김)' : ''}
            </option>
          ))}
        </select>

        {isHidden && (
          <span className="rounded-md bg-neutral-800 px-2 py-1 text-[11.5px] font-bold text-white dark:bg-white/15">
            숨김 — 일반 사용자에게 안 보입니다
          </span>
        )}

        {current && !renaming && (
          <button
            type="button"
            ref={renameRef}
            className={BTN_GHOST}
            onClick={() => {
              setLabel(current.label);
              setRenaming(true);
            }}
          >
            이름 수정
          </button>
        )}

        {current && renaming && (
          <>
            <input
              className={`${FIELD} w-44`}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              aria-label="개정판 표기 이름"
            />
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={!label.trim() || updateEdition.isPending}
              onClick={() =>
                updateEdition.mutate(
                  { slug: current.slug, patch: { label: label.trim() } },
                  {
                    onSuccess: () => {
                      setRenaming(false);
                      renameRef.current?.focus();
                    },
                    onError,
                  },
                )
              }
            >
              저장
            </button>
            <button
              type="button"
              className={BTN_GHOST}
              onClick={() => {
                setRenaming(false);
                renameRef.current?.focus();
              }}
            >
              취소
            </button>
          </>
        )}

        {!adding ? (
          <button
            type="button"
            ref={addRef}
            className={BTN_GHOST}
            onClick={openAddForm}
          >
            판 추가
          </button>
        ) : (
          <>
            <input
              className={`${FIELD} w-24`}
              type="number"
              value={year}
              aria-label="연도"
              onChange={(event) => setYear(Number(event.target.value))}
            />
            <select
              className={`${FIELD} w-28`}
              value={half}
              aria-label="반기"
              onChange={(event) => setHalf(event.target.value as 'H1' | 'H2')}
            >
              <option value="H1">상반기</option>
              <option value="H2">하반기</option>
            </select>
            <select
              className={`${FIELD} w-auto min-w-44`}
              value={copyFrom}
              aria-label="복사할 개정판"
              onChange={(event) => setCopyFrom(event.target.value)}
            >
              <option value="">빈 판으로 시작</option>
              {editions.map((item) => (
                <option key={item.slug} value={item.slug}>
                  {item.label} 복사
                </option>
              ))}
            </select>
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={
                createEdition.isPending ||
                !Number.isInteger(year) ||
                year < MIN_YEAR
              }
              onClick={() =>
                createEdition.mutate(
                  { year, half, copyFrom: copyFrom || undefined },
                  {
                    onSuccess: (created) => {
                      setAdding(false);
                      addRef.current?.focus();
                      onSelect(created.slug);
                    },
                    onError,
                  },
                )
              }
            >
              {createEdition.isPending ? '만드는 중…' : '만들기'}
            </button>
            <button
              type="button"
              className={BTN_GHOST}
              onClick={() => {
                setAdding(false);
                addRef.current?.focus();
              }}
            >
              취소
            </button>
          </>
        )}

        {current && (
          <span className="ml-auto flex gap-2">
            {/* 숨기기는 공개 중인 판을 내리는 동작이다 — 지금 그 판을 보고
                있는 모든 사용자가 다른 판으로 넘어간다. 클릭 **전에** 그걸
                말해야 한다. 반대 방향(공개하기)은 되돌리기 쉬워 즉시 나간다. */}
            {!isHidden && confirmingHide ? (
              <>
                <button
                  type="button"
                  className={BTN_DANGER}
                  disabled={setVisibility.isPending}
                  onClick={() =>
                    setVisibility.mutate(
                      { slug: current.slug, visible: false },
                      { onSuccess: () => setConfirmingHide(false), onError },
                    )
                  }
                >
                  {setVisibility.isPending ? '숨기는 중…' : '정말 숨기기'}
                </button>
                <button
                  type="button"
                  className={BTN_GHOST}
                  onClick={() => setConfirmingHide(false)}
                >
                  취소
                </button>
              </>
            ) : (
              <button
                type="button"
                className={isHidden ? BTN_PRIMARY : BTN_GHOST}
                disabled={setVisibility.isPending}
                onClick={() => {
                  setConfirmingDelete(false);
                  if (isHidden) {
                    setVisibility.mutate(
                      { slug: current.slug, visible: true },
                      { onError },
                    );
                    return;
                  }
                  setConfirmingHide(true);
                }}
              >
                {isHidden ? '공개하기' : '숨기기'}
              </button>
            )}

            {/* 삭제는 숨긴 판에서만. 공개 중인 판은 서버도 거절한다. */}
            {isHidden &&
              (confirmingDelete ? (
                <>
                  <button
                    type="button"
                    className={BTN_DANGER}
                    disabled={removeEdition.isPending}
                    onClick={() =>
                      removeEdition.mutate(current.slug, {
                        onSuccess: () => {
                          setConfirmingDelete(false);
                          onSelect(null);
                        },
                        onError,
                      })
                    }
                  >
                    {removeEdition.isPending ? '삭제 중…' : '정말 삭제'}
                  </button>
                  <button
                    type="button"
                    className={BTN_GHOST}
                    onClick={() => setConfirmingDelete(false)}
                  >
                    취소
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={BTN_DANGER}
                  onClick={() => setConfirmingDelete(true)}
                >
                  판 삭제
                </button>
              ))}
          </span>
        )}
      </div>

      {adding && (
        <p className="mt-2 text-[12px] text-neutral-500">
          복사하면 고른 판의 <b>부문 편성·장·절·항목</b>이 그대로 새 판에
          들어옵니다. 개정 표기는 초기화되고, 이후 고친 항목만 개정으로 잡힙니다.
        </p>
      )}

      {confirmingHide && current && (
        <p className="mt-2 text-[13px] text-danger">
          {current.label} 을(를) 숨기면 지금 이 판을 보고 있는 사용자는{' '}
          <b>{fallback ? fallback.label : '아무 판도'}</b> 을(를) 보게 됩니다.
          {!fallback && ' — 공개된 판이 하나도 남지 않습니다.'} 판 고정 링크
          (<code>?edition={current.slug}</code>)는 열리지 않게 됩니다.
        </p>
      )}

      {/* 무엇이 함께 사라지는지 숫자로 말한다 — "정말 삭제할까요?" 만으로는
          이 판에 1,284건이 들어 있다는 걸 알 수 없다. */}
      {confirmingDelete && current && (
        <p className="mt-2 text-[13px] text-danger">
          {current.label} 과(와) 그 아래 <b>항목 {itemCount}건</b>·장·절이 함께
          삭제됩니다. 항목 자체와 다른 판의 내용, 딥링크는 남습니다.
        </p>
      )}

      {isHidden && !confirmingDelete && (
        <p className="mt-2 text-[12px] text-neutral-500">
          숨긴 판입니다. 관리자에게만 보이고, 일괄 임포트와 장·절 순서 변경도
          숨긴 동안에만 됩니다.
        </p>
      )}

      {error && (
        <p className="mt-2 text-[13px] text-danger" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
